/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  CreateOauthClientsData,
  CreateOauthClientsResponse,
  OauthClientSortableFields,
  PaginatedOauthClientsResponseSchema,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

// OAuth client filters schema - embedded in response meta, defined inline
const $OauthClientFilters = {
  properties: {
    name: { type: 'string' },
  },
} as const;

// Define the filters type based on the schema
export type OauthClientFilters = {
  name?: string;
};

// Type for individual OAuth client from paginated response
export type OAuthClient = PaginatedOauthClientsResponseSchema['data'][number];

export type UsePaginatedOAuthClientsQueryParams = {
  params?: Omit<CreateOauthClientsData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated OAuth clients with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated OAuth clients response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedOAuthClientsQuery({ params, type }: UsePaginatedOAuthClientsQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<
    CreateOauthClientsResponse,
    OAuthClient,
    OauthClientFilters,
    OauthClientSortableFields,
    CreateOauthClientsData
  >({
    apiCall: mgmtPlaneAPIClient.oauthClients.createOauthClients.bind(mgmtPlaneAPIClient.oauthClients),
    filterTypes: jsonSchemaToTypeMap($OauthClientFilters),
    queryKey: 'oauthClients',
    realtimeEvents: ['oauth-client:created', 'oauth-client:updated', 'oauth-client:deleted'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
